import { useQuery } from "@tanstack/react-query";
import { Camera, Images } from "lucide-react";
import { useId, useRef, useState } from "react";

import { Button } from "@buildpy/ui/button";
import { viewQuery } from "@/api/views";
import { queryClient } from "@/lib/query";
import { uploadUsageLineOf } from "@/modules/plan/upload-usage";
import { photoPhaseOf } from "@buildpy/core/modules/project/photo-phase";
import { usePhotoUpload } from "@/modules/project/ui/use-photo-upload";

interface PhaseOption {
  id: string;
  name: string;
}

interface Props {
  projectId: string;
  logDate: string;
  // 그날 고를 수 있는 공정 — 고르면 사진에 공정 태그가 붙는다(선택).
  phases: readonly PhaseOption[];
  defaultPhaseId: string | null;
}

// 사진 올리기 — 카메라(큰 버튼)·앨범(여러 장). 한 장씩 줄여서 차례로 올린다.
const PhotoUploadButton = ({ projectId, logDate, phases, defaultPhaseId }: Props) => {
  const cameraRef = useRef<HTMLInputElement>(null);
  const albumRef = useRef<HTMLInputElement>(null);
  const { progress, uploadAll } = usePhotoUpload(projectId, logDate);
  // undefined = 아직 안 고름(기본값을 따른다) · null = 「공정 없음」을 고름.
  const [choice, setChoice] = useState<string | null | undefined>(undefined);
  const { id: phaseId, lost } = photoPhaseOf(phases, choice, defaultPhaseId);
  const labelId = useId();
  // 용량 줄(대표만) — 셸은 /app 이 이미 읽어 둔 캐시만 본다(새 요청 없음) · 올린 뒤 refresh 가 셸도 다시 읽어 숫자가 따라온다.
  const { data: shell } = useQuery({ ...viewQuery("shell"), enabled: false }, queryClient);
  const storage = shell?.member.role === "owner" ? shell.planUsage?.storage : null;
  const usageLine = uploadUsageLineOf(storage);

  const upload = async (list: FileList | null) => {
    try {
      await uploadAll(list, phaseId);
    } finally {
      if (cameraRef.current) cameraRef.current.value = "";
      if (albumRef.current) albumRef.current.value = "";
    }
  };

  const busy = progress !== null;

  return (
    <div className="flex flex-col gap-y-2">
      {phases.length > 0 && (
        <div className="flex flex-col gap-y-1 text-xs" data-testid="today-photo-phase">
          <span className="font-medium text-foreground" id={labelId}>
            이 사진의 공정
          </span>
          <div aria-labelledby={labelId} className="flex flex-wrap items-center gap-1.5" role="group">
            {[{ id: "", name: "공정 없음" }, ...phases].map((ph) => {
              const selected = (phaseId ?? "") === ph.id;
              return (
                <Button
                  key={ph.id || "none"}
                  aria-pressed={selected}
                  disabled={busy}
                  size="chip"
                  type="button"
                  variant="chip"
                  onClick={() => setChoice(ph.id || null)}>
                  {ph.name}
                </Button>
              );
            })}
          </div>
          <p className="text-muted-foreground">고르면 사진이 그 공정으로 묶입니다 — 그날 진행한 공정이 하나로 확실할 때만 미리 골라 둡니다.</p>
        </div>
      )}
      {lost && (
        <p className="text-xs text-muted-foreground" data-testid="today-photo-phase-lost" role="status">
          고른 공정이 목록에서 빠져 「공정 없음」으로 두었습니다.
        </p>
      )}

      <Button
        className="w-full"
        data-testid="today-photo-button"
        disabled={busy}
        size="field"
        variant="primary"
        onClick={() => cameraRef.current?.click()}>
        <Camera />
        {progress ? `올리는 중 ${progress.done}/${progress.total}` : "사진 올리기"}
      </Button>
      {/* mt-1 — 폰 tap-target(40px)이 위 카메라 버튼을 덮지 않게 gap-y-2 에 더해 12px 띄운다 */}
      <Button
        className="mt-1 self-center"
        disabled={busy}
        size="xs-touch"
        type="button"
        variant="link-muted"
        onClick={() => albumRef.current?.click()}>
        <Images className="size-3.5" />
        앨범에서 여러 장 고르기
      </Button>
      {usageLine && (
        <p className={usageLine.level === "over" ? "text-xs text-destructive" : "text-xs text-warning"} data-level={usageLine.level} data-testid="today-photo-usage">
          {usageLine.text}
        </p>
      )}

      <input
        ref={cameraRef}
        accept="image/*"
        aria-label="카메라로 사진 찍기"
        capture="environment"
        className="hidden"
        type="file"
        onChange={(e) => void upload(e.target.files)}
      />
      <input
        ref={albumRef}
        multiple
        accept="image/*"
        aria-label="앨범에서 사진 고르기"
        className="hidden"
        type="file"
        onChange={(e) => void upload(e.target.files)}
      />
    </div>
  );
};

export default PhotoUploadButton;
